// ComplaintsBoard.jsx - Complaint Management Component
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Download, RefreshCw, FileText, X, MessageSquare,
    Clock, CheckCircle, UserCheck, Eye, Edit, Trash2, Save,
    ChevronLeft, ChevronRight, Search, AlertCircle, Users, Shield,
    Loader2,
} from 'lucide-react';
import { canReply, canView, canEdit, canDelete, canExport } from '../../view/Permissions';
import {
    fetchUserQueries,
    updateUserQuery,
    deleteUserQuery,
    fetchMasterQueries,
    updateMasterQuery,
    deleteMasterQuery,
    mapComplaint,
    STATUS_OPTIONS,
    STATUS_LABEL_TO_API,
    STATUS_API_TO_LABEL,
} from './ComplaintAPI';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 5;

const STATUS_COLORS = {
    'pending': { bg: 'rgba(139,92,246,0.15)', text: '#8b5cf6' },
    'in progress': { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
    'resolved': { bg: 'rgba(52,211,153,0.15)', text: '#34d399' },
};


const DATA_SOURCES = {
    user: {
        label: 'User',
        fetch: fetchUserQueries,
        update: updateUserQuery,
        delete: deleteUserQuery,
        icon: UserCheck,
    },
    master: {
        label: 'Master',
        fetch: fetchMasterQueries,
        update: updateMasterQuery,
        delete: deleteMasterQuery,
        icon: Shield,
    },
};



const exportComplaintsToCSV = (rows, sourceLabel) => {
    if (!rows || rows.length === 0) return;

    const headersRow = ['ID', 'Ticket ID', 'Title', 'Description', 'User', 'Status', 'Reply', 'Raised'];

    const escapeCsvValue = (value) => {
        const str = value === null || value === undefined ? '' : String(value);
        if (/[",\n]/.test(str)) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const csvRows = rows.map((c) =>
        [c.id, c.ticketId || '', c.title, c.description, c.user, c.status, c.reply || '', c.time]
            .map(escapeCsvValue)
            .join(',')
    );

    const csvContent = [headersRow.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.setAttribute('download', `${sourceLabel.toLowerCase()}-complaints-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const Panel = ({ children, className = '' }) => (
    <div
        className={`rounded-2xl p-5 bg-white ${className}`}
        style={{ border: '1px solid rgba(255,125,56,0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
    >
        {children}
    </div>
);

const StatCard = ({ label, value, icon: Icon, color, link }) => {
    const navigate = useNavigate();
    return (
        <>
            <div onClick={() => navigate(link)} className="relative overflow-hidden rounded-2xl p-5 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg bg-white border border-orange-200 shadow-sm cursor-pointer">
                <div className="absolute -top-8 -left-8 w-20 h-20 rounded-full bg-orange-400/10" />
                <div className="absolute -top-4 -left-4 w-28 h-28 rounded-full border border-orange-300/20" />
                <div className="absolute -bottom-4 -right-4 w-12 h-12 rounded-full bg-orange-400/10" />
                <div className="relative z-10 flex items-start justify-between">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
                        <p className="text-2xl font-bold mt-1 text-gray-800">{value}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg shrink-0`}>
                        <Icon size={18} className="text-white" />
                    </div>
                </div>
            </div>
        </>
    )

};


const SourceNav = ({ source, onChange }) => (
    <div className="flex items-center gap-1.5 bg-white p-1.5 rounded-xl border border-orange-200 w-fit shadow-sm">
        {Object.entries(DATA_SOURCES).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const active = source === key;
            return (
                <button
                    key={key}
                    onClick={() => onChange(key)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${active
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-orange-50'
                        }`}
                >
                    <Icon size={14} /> {cfg.label}
                </button>
            );
        })}
    </div>
);


const ComplaintModal = ({ complaint, isOpen, onClose, onSave, hasEditPermission, saving }) => {
    const [replyDraft, setReplyDraft] = useState('');
    const [statusDraft, setStatusDraft] = useState('Pending');

    useEffect(() => {
        if (complaint) {
            setReplyDraft(complaint.reply || '');
            setStatusDraft(complaint.status);
        }
    }, [complaint]);

    if (!isOpen || !complaint) return null;

    const statusStyle = STATUS_COLORS[statusDraft.toLowerCase()] || STATUS_COLORS['pending'];
    const isDirty = statusDraft !== complaint.status || replyDraft !== (complaint.reply || '');

    const handleSave = () => {
        onSave({
            id: complaint.id,
            status: STATUS_LABEL_TO_API[statusDraft],
            reply: replyDraft,
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col bg-white border border-orange-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-orange-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-50">
                            <FileText size={20} className="text-orange-500" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Query Details</h2>
                            <p className="text-xs text-gray-500">
                                Ticket: {complaint.ticketId || 'N/A'} · ID: {complaint.id}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg transition-colors hover:bg-orange-50 text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    <div className="flex flex-wrap items-center gap-2">
                        {hasEditPermission ? (
                            <select
                                value={statusDraft}
                                onChange={(e) => setStatusDraft(e.target.value)}
                                className="text-xs font-medium px-2.5 py-1.5 rounded-full border focus:outline-none"
                                style={{ background: statusStyle.bg, color: statusStyle.text, borderColor: statusStyle.text + '40' }}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        ) : (
                            <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: statusStyle.bg, color: statusStyle.text }}>
                                {statusDraft}
                            </span>
                        )}
                    </div>

                    <div>
                        <h3 className="text-sm font-semibold text-gray-800">{complaint.title}</h3>
                        <p className="text-sm mt-2 text-gray-600 whitespace-pre-wrap">{complaint.description}</p>
                    </div>

                    {complaint.image && (
                        <img
                            src={complaint.image}
                            alt="attachment"
                            className="w-full max-h-64 object-contain rounded-xl border border-orange-100 bg-orange-50"
                        />
                    )}

                    <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-orange-50 border border-orange-200">
                        <div>
                            <p className="text-xs text-gray-500">User</p>
                            <p className="text-sm font-medium text-gray-700">{complaint.user}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Raised</p>
                            <p className="text-sm font-medium text-gray-700">{complaint.time}</p>
                        </div>
                        {complaint.reason && (
                            <div className="col-span-2">
                                <p className="text-xs text-gray-500">Reason</p>
                                <p className="text-sm font-medium text-gray-700">{complaint.reason}</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500">Admin Reply</label>
                        {hasEditPermission ? (
                            <textarea
                                value={replyDraft}
                                onChange={(e) => setReplyDraft(e.target.value)}
                                rows={4}
                                placeholder="Write a reply to the user..."
                                className="w-full mt-1 px-3 py-2 text-sm rounded-xl border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 resize-none"
                            />
                        ) : (
                            <p className="text-sm mt-1 text-gray-700">{complaint.reply || 'No reply yet'}</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-orange-100 rounded-b-2xl">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-xl transition-colors text-gray-600 hover:bg-gray-50">
                        Close
                    </button>
                    {hasEditPermission && (
                        <button
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-xl transition-all bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};


const ComplaintCard = ({ complaint, onViewDetails, onStatusChange, onDelete, hasReplyPermission, hasEditPermission, hasViewPermission, hasDeletePermission, saving, deleting }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(complaint.status);

    useEffect(() => { setSelectedStatus(complaint.status); }, [complaint.status]);

    const statusStyle = STATUS_COLORS[complaint.status.toLowerCase()] || STATUS_COLORS['pending'];

    const handleSaveStatus = () => {
        if (selectedStatus !== complaint.status) {
            onStatusChange(complaint, selectedStatus);
        }
        setIsEditing(false);
    };

    return (
        <div className="rounded-xl p-4 border transition-all duration-200 hover:border-orange-300 bg-white border-orange-200 shadow-sm hover:shadow-md">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-500">{complaint.ticketId || complaint.id}</span>
                    {complaint.image && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-500">
                            Has attachment
                        </span>
                    )}
                </div>
                <span className="text-xs flex items-center gap-1 text-gray-500">
                    <Clock size={12} /> {complaint.time}
                </span>
            </div>

            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-800">{complaint.title}</h4>
                    <p className="text-xs mt-1 line-clamp-2 text-gray-600">{complaint.description}</p>
                </div>

                {isEditing ? (
                    <div className="flex items-center gap-1 shrink-0">
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="text-[10px] font-medium px-2 py-1 rounded-lg border border-orange-200 bg-white text-gray-700 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                        <button onClick={handleSaveStatus} disabled={saving} className="p-1 rounded-lg transition-colors bg-green-50 text-green-500 hover:bg-green-100 disabled:opacity-50">
                            <Save size={14} />
                        </button>
                        <button
                            onClick={() => { setIsEditing(false); setSelectedStatus(complaint.status); }}
                            className="p-1 rounded-lg transition-colors bg-gray-50 text-gray-500 hover:bg-gray-100"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] font-medium px-2.5 py-1 rounded-full" style={{ background: statusStyle.bg, color: statusStyle.text }}>
                            {complaint.status}
                        </span>
                        {hasEditPermission && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="p-1 rounded-lg transition-colors text-gray-400 hover:text-orange-500 hover:bg-orange-50"
                                title="Edit Status"
                            >
                                <Edit size={14} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <span className="text-gray-600">
                        <UserCheck size={12} className="inline mr-1 text-gray-400" />
                        {complaint.user}
                    </span>
                    {complaint.reply && (
                        <span className="text-gray-600">
                            <MessageSquare size={12} className="inline mr-1 text-gray-400" />
                            Replied
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {hasViewPermission && (
                        <button
                            onClick={() => onViewDetails(complaint)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100"
                        >
                            <Eye size={12} /> View
                        </button>
                    )}
                    {hasReplyPermission && (
                        <button
                            onClick={() => onViewDetails(complaint)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium bg-blue-50 text-blue-500 border border-blue-200 hover:bg-blue-100"
                        >
                            <Edit size={12} /> Reply
                        </button>
                    )}
                    {hasDeletePermission && (
                        <button
                            onClick={() => {
                                if (window.confirm('Delete this complaint permanently? This cannot be undone.')) {
                                    onDelete(complaint.id);
                                }
                            }}
                            disabled={deleting}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Trash2 size={12} /> {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};


const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
    const getPageNumbers = useCallback(() => {
        const pages = [];
        const maxVisible = 5;
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else if (currentPage <= 3) {
            for (let i = 1; i <= 4; i++) pages.push(i);
            pages.push('...', totalPages);
        } else if (currentPage >= totalPages - 2) {
            pages.push(1, '...');
            for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1, '...');
            for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
            pages.push('...', totalPages);
        }
        return pages;
    }, [currentPage, totalPages]);

    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-orange-200">
            <div className="text-xs text-gray-500">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} complaints
            </div>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-orange-50 border border-orange-200 text-gray-500 hover:bg-orange-100 hover:text-orange-500"
                >
                    <ChevronLeft size={16} />
                </button>
                {getPageNumbers().map((page, index) => (
                    <button
                        key={index}
                        onClick={() => typeof page === 'number' && onPageChange(page)}
                        disabled={page === '...' || page === currentPage}
                        className={`min-w-[32px] h-8 px-2 rounded-lg text-xs transition-all ${page === currentPage ? 'font-semibold bg-orange-500 text-white shadow-md' : 'text-gray-600 hover:bg-orange-50'}`}
                    >
                        {page}
                    </button>
                ))}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-orange-50 border border-orange-200 text-gray-500 hover:bg-orange-100 hover:text-orange-500"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};


const ComplaintsBoard = ({ statusFilter, title, subtitle}) => {
    const [source, setSource] = useState('user'); 
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [pageName, setPageName] = useState('Pending Complaints');

    useEffect(() => {
        switch (statusFilter) {
            case 'pending':
                setPageName('Pending Complaints');
                break;
            case 'solved':
                setPageName('Resolved Complaints');
                break;
            case 'inprogress':
                setPageName('In Progress Complaints');
                break;
            default:
                setPageName('All Complaints');
        }
    }, [statusFilter]);

    
    const hasReplyPermission = canReply(pageName);
    const hasViewPermission = canView(pageName);
    const hasEditPermission = canEdit(pageName);
    const hasDeletePermission = canDelete(pageName);
    const hasExportPermission = canExport(pageName);
    
    const showToast = (type, message) => {
        if (type === 'success') {
            toast.success(message);
        } else {
            toast.error(message);
        }
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await DATA_SOURCES[source].fetch('All');
            if (res && res.success) {
                setComplaints((res.data || []).map(mapComplaint));
            } else {
                throw new Error(res?.message || 'Failed to load complaints');
            }
        } catch (err) {
            console.error('fetch queries failed:', err);
            setError(err?.response?.data?.message || err?.message || 'Failed to load complaints');
            setComplaints([]);
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load complaints');
        } finally {
            setLoading(false);
        }
    }, [source]);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, source]);

    const handleSourceChange = (nextSource) => {
        if (nextSource === source) return;
        setSource(nextSource);
        setSearchTerm('');
        setIsModalOpen(false);
        setSelectedComplaint(null);
    };

    const handleViewComplaint = (complaint) => {
        setSelectedComplaint(complaint);
        setIsModalOpen(true);
    };

    const persistUpdate = async ({ id, status, reply }) => {
        setSavingId(id);
        try {
            const res = await DATA_SOURCES[source].update({ id, status, reply });
            if (!res || !res.success) {
                throw new Error(res?.message || 'Update failed');
            }

            
            setComplaints((prev) =>
                prev.map((c) =>
                    c.id === id
                        ? {
                            ...c,
                            apiStatus: status,
                            status: STATUS_API_TO_LABEL[status] || status,
                            reply: reply !== undefined ? reply : c.reply,
                        }
                        : c
                )
            );

            showToast('success', res.message || 'Updated successfully');
            setIsModalOpen(false);
            setSelectedComplaint(null);
        } catch (err) {
            console.error('update query failed:', err);
            showToast('error', err?.response?.data?.message || err?.message || 'Update failed');
        } finally {
            setSavingId(null);
        }
    };

    const handleInlineStatusChange = (complaint, newStatusLabel) => {
        persistUpdate({ id: complaint.id, status: STATUS_LABEL_TO_API[newStatusLabel] });
    };

    const handleModalSave = (payload) => persistUpdate(payload);

    const handleDeleteComplaint = async (id) => {
        setDeletingId(id);
        try {
            const res = await DATA_SOURCES[source].delete(id);
            if (!res || !res.success) {
                throw new Error(res?.message || 'Delete failed');
            }
            setComplaints((prev) => prev.filter((c) => c.id !== id));
            showToast('success', res.message || 'Complaint deleted');
        } catch (err) {
            console.error('delete query failed:', err);
            showToast('error', err?.response?.data?.message || err?.message || 'Delete failed');
        } finally {
            setDeletingId(null);
        }
    };

    const filteredComplaints = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return complaints.filter((c) => {
            const matchesStatus = statusFilter === 'All' || c.apiStatus === statusFilter;
            const matchesSearch =
                c.title.toLowerCase().includes(term) ||
                c.user.toLowerCase().includes(term) ||
                c.id.toLowerCase().includes(term) ||
                (c.ticketId || '').toLowerCase().includes(term) ||
                c.description.toLowerCase().includes(term);
            return matchesStatus && matchesSearch;
        });
    }, [complaints, searchTerm, statusFilter]);

    const paginatedComplaints = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredComplaints.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredComplaints, currentPage]);

    const totalPages = Math.ceil(filteredComplaints.length / ITEMS_PER_PAGE);

    const stats = useMemo(() => ({
        total: complaints.length,
        pending: complaints.filter((c) => c.apiStatus === 'pending').length,
        inProgress: complaints.filter((c) => c.apiStatus === 'inprogress').length,
        resolved: complaints.filter((c) => c.apiStatus === 'solved').length,
    }), [complaints]);

    
    const handleExport = () => {
        if (exporting || filteredComplaints.length === 0) return;
        setExporting(true);
        try {
            exportComplaintsToCSV(filteredComplaints, DATA_SOURCES[source].label);
            toast.success('Complaints Exported successfully!');
        } catch (err) {
            toast.error('Failed to export data. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div>
                    <h1 className="text-xl font-bold text-gray-800">{title}</h1>
                    <p className="text-sm mt-0.5 text-gray-500">{subtitle}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={loadData}
                        className="flex items-center gap-2 px-3.5 py-2 text-sm rounded-xl transition-colors bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100"
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                    {hasExportPermission && (
                        <button
                            onClick={handleExport}
                            disabled={exporting || filteredComplaints.length === 0}
                            className="flex items-center gap-2 px-3.5 py-2 text-sm text-white rounded-xl transition-all bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export
                        </button>
                    )}
                </div>
            </div>

            {/* User / Master nav */}
            <SourceNav source={source} onChange={handleSourceChange} />

            {error && (
                <div className="rounded-2xl p-4 bg-red-50 border border-red-200 text-sm text-red-600 flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
                    <button onClick={loadData} className="underline font-medium shrink-0">Retry</button>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center min-h-[300px] bg-white rounded-2xl border border-orange-200">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-400 border-t-transparent mx-auto" />
                        <p className="mt-4 text-sm text-gray-500">Loading {DATA_SOURCES[source].label.toLowerCase()} complaints...</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <StatCard label="Total" value={stats.total} icon={MessageSquare} color="from-amber-400 to-orange-500" link={'/admin/complaints/all'} />
                        <StatCard label="Pending" value={stats.pending} icon={Clock} color="from-orange-400 to-amber-500" link={'/admin/complaints/pending'} />
                        <StatCard label="In Progress" value={stats.inProgress} icon={RefreshCw} color="from-amber-400 to-yellow-500" link={'/admin/complaints/in-progress'} />
                        <StatCard label="Resolved" value={stats.resolved} icon={CheckCircle} color="from-orange-400 to-amber-500" link={'/admin/complaints/resolved'} />
                    </div>

                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by title, user, ticket ID, or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl transition-all bg-white border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                        />
                    </div>

                    {/* List */}
                    <Panel>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                            <h3 className="text-sm font-semibold text-gray-800">
                                {title} ({filteredComplaints.length}) · {DATA_SOURCES[source].label}
                            </h3>
                            <span className="text-xs text-gray-500">Showing most recent first</span>
                        </div>
                        {paginatedComplaints.length === 0 ? (
                            <div className="text-center py-8">
                                <MessageSquare size={40} className="mx-auto mb-3 opacity-20 text-gray-400" />
                                <p className="text-gray-500">No complaints found{searchTerm ? ' matching your search' : ''}</p>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-3">
                                    {paginatedComplaints.map((complaint) => (
                                        <ComplaintCard
                                            key={complaint.id}
                                            complaint={complaint}
                                            onViewDetails={handleViewComplaint}
                                            onStatusChange={handleInlineStatusChange}
                                            onDelete={handleDeleteComplaint}
                                            hasReplyPermission={hasReplyPermission}
                                            hasEditPermission={hasEditPermission}
                                            hasViewPermission={hasViewPermission}
                                            hasDeletePermission={hasDeletePermission}
                                            saving={savingId === complaint.id}
                                            deleting={deletingId === complaint.id}
                                        />
                                    ))}
                                </div>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    totalItems={filteredComplaints.length}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                />
                            </>
                        )}
                    </Panel>

                    {/* Footer */}
                    <div className="rounded-2xl px-6 py-4 flex flex-wrap items-center justify-between gap-3 bg-white border border-orange-200 shadow-sm">
                        <span className="text-xs text-gray-500">Last updated: {new Date().toLocaleString()}</span>
                        <span className="text-xs text-gray-500">Total complaints: {complaints.length}</span>
                    </div>
                </>
            )}

            {/* Modal: view + edit reply/status */}
            <ComplaintModal
                complaint={selectedComplaint}
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setSelectedComplaint(null); }}
                onSave={handleModalSave}
                hasEditPermission={hasEditPermission}
                saving={savingId === selectedComplaint?.id}
            />
        </div>
    );
};

export default ComplaintsBoard;